(function () {
  'use strict';

  const UNKNOWN = 'Room dimensions for this chair are being confirmed. Please contact our team for a precise space check.';
  const DEMO_VIEWER_URL = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
  let viewerPromise;
  let qrPromise;
  let activeModal;

  function requirements(dimensions) {
    if (!dimensions) return null;
    const { chairWidth, chairHeight, chairDepth, reclinedDepth, wallClearance, minWidth, minDepth } = dimensions;
    if (![chairWidth, chairHeight, chairDepth, reclinedDepth, minWidth, minDepth]
      .every(value => Number.isFinite(value) && value > 0) ||
      !Number.isFinite(wallClearance) || wallClearance < 0) return null;
    return {
      width: Math.max(chairWidth, minWidth),
      depth: Math.max(chairDepth, minDepth, reclinedDepth + wallClearance)
    };
  }

  function evaluate(dimensions, width, depth) {
    if (![width, depth].every(value => Number.isFinite(value) && value > 0 && value <= 2000)) {
      return { status: 'invalid', message: 'Enter positive room width and depth in centimetres (up to 2000 cm).' };
    }
    const required = requirements(dimensions);
    if (!required) return { status: 'unknown', message: UNKNOWN };
    const widthShort = Math.max(0, required.width - width);
    const depthShort = Math.max(0, required.depth - depth);
    if (widthShort || depthShort) {
      const shortfalls = [];
      if (widthShort) shortfalls.push(Math.ceil(widthShort) + ' cm more width');
      if (depthShort) shortfalls.push(Math.ceil(depthShort) + ' cm more depth');
      return { status: 'more-space', message: 'You may need approximately ' + shortfalls.join(' and ') + ' for full operation.', required };
    }
    if (width - required.width < 10 || depth - required.depth < 10) {
      return { status: 'tight', message: 'This chair may fit, but we recommend additional clearance for comfortable use.', required };
    }
    return { status: 'great', message: 'This chair fits within your available space.', required };
  }

  function handoffUrl(arUrl, currentUrl, previewOnly = false, draftThemeId = null) {
    const target = new URL(arUrl, currentUrl);
    if (target.protocol !== 'https:' && !(previewOnly && target.protocol === 'http:')) {
      throw new Error('An HTTPS storefront is required for native AR');
    }
    if (!previewOnly) {
      const previewThemeId = new URL(currentUrl).searchParams.get('preview_theme_id') || draftThemeId;
      if (previewThemeId) target.searchParams.set('preview_theme_id', previewThemeId);
      target.searchParams.set('jc_roomfit_ar', '1');
      target.hash = 'jc-roomfit';
    }
    return target;
  }

  function galleryHandoffUrl(galleryUrl, currentUrl, draftThemeId = null) {
    const source = new URL(currentUrl);
    const target = new URL(galleryUrl, source);
    const localPreview = target.origin === source.origin &&
      /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+|10\.\d+\.\d+)$/.test(source.hostname);
    if (target.protocol !== 'https:' && !(localPreview && target.protocol === 'http:')) {
      throw new Error('An HTTPS storefront is required for the RoomFit gallery');
    }
    const previewThemeId = source.searchParams.get('preview_theme_id') || draftThemeId;
    if (previewThemeId) target.searchParams.set('preview_theme_id', previewThemeId);
    return target;
  }

  const Engine = { evaluate, requirements, handoffUrl, galleryHandoffUrl };
  if (typeof module !== 'undefined' && module.exports) module.exports = Engine;
  if (typeof document === 'undefined') return;

  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }
  function demoViewer() {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!viewerPromise) {
      viewerPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = DEMO_VIEWER_URL;
        script.onload = () => customElements.whenDefined('model-viewer').then(resolve, reject);
        script.onerror = () => reject(new Error('3D viewer failed to load'));
        document.head.append(script);
      });
    }
    return viewerPromise;
  }
  function canUseMobileAR() {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }
  function loadQr(scriptUrl) {
    if (!qrPromise) {
      qrPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.onload = () => typeof window.qrcode === 'function'
          ? resolve(window.qrcode) : reject(new Error('QR generator is unavailable'));
        script.onerror = () => reject(new Error('QR generator could not load'));
        document.head.append(script);
      }).catch(error => { qrPromise = null; throw error; });
    }
    return qrPromise;
  }
  function open(product, mode, origin) {
    if (activeModal) activeModal.remove();
    const overlay = node('div', 'jcrf-overlay');
    const dialog = node('div', 'jcrf-dialog');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    const heading = node('h2', '', mode === 'qr'
      ? product.galleryUrl ? 'Explore chairs in your room' : 'View in Room'
      : mode === 'model' ? 'View chair in 3D' : 'Check My Room Fit');
    heading.id = 'jcrf-heading';
    dialog.setAttribute('aria-labelledby', heading.id);
    const close = node('button', 'jcrf-close', 'Close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close RoomFit dialog');
    const content = node('div', 'jcrf-content');
    dialog.append(close, heading, node('p', 'jcrf-subtitle', product.galleryUrl ? 'JC Buckman Room Preview' : product.title), content);
    overlay.append(dialog);
    document.body.append(overlay);
    activeModal = overlay;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function dismiss() {
      overlay.remove();
      if (activeModal === overlay) activeModal = null;
      document.body.style.overflow = oldOverflow;
      origin.focus();
    }
    close.addEventListener('click', dismiss);
    overlay.addEventListener('click', event => { if (event.target === overlay) dismiss(); });
    overlay.addEventListener('keydown', event => {
      if (event.key === 'Escape') dismiss();
      if (event.key === 'Tab') {
        const controls = Array.from(dialog.querySelectorAll('button, input, a[href], model-viewer')).filter(item => !item.disabled);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    });
    close.focus();
    if (mode === 'qr') {
      if ((!product.model || !product.arUrl) && !product.galleryUrl) {
        console.error('JC RoomFit: a Shopify product model and URL or gallery URL are required for the QR handoff');
        content.append(node('p', 'jcrf-message', 'View in Room is not available for this chair yet.'));
        return;
      }
      let url;
      try {
        const theme = window.Shopify && window.Shopify.theme;
        const draftThemeId = theme && theme.role === 'unpublished' ? theme.id : null;
        url = product.galleryUrl
          ? galleryHandoffUrl(product.galleryUrl, window.location.href, draftThemeId)
          : handoffUrl(product.arUrl, window.location.href, product.previewOnly, draftThemeId);
      } catch (error) {
        console.error('JC RoomFit: QR handoff URL unavailable', error);
        content.append(node('p', 'jcrf-message', 'View in Room requires a valid secure Shopify product page.'));
        return;
      }
      if ((product.previewOnly || product.galleryUrl && url.protocol === 'http:') &&
        /^(localhost|127\.0\.0\.1)$/.test(url.hostname)) {
        content.append(node('p', 'jcrf-message',
          'For a phone QR preview, open this page on your computer using its Wi-Fi IP address instead of localhost, then try again.'));
        return;
      }
      content.append(node('p', 'jcrf-message', product.galleryUrl
        ? url.protocol === 'http:'
          ? 'Scan on the same Wi-Fi to open the local chair gallery. Local HTTP supports 3D preview only, not camera AR.'
          : 'Scan to explore our AR-ready chairs. On a supported phone, tap a chair to place it in your room.'
        : product.previewOnly
        ? 'Scan with a phone on the same Wi-Fi to open this local 3D preview. Camera AR is not available over local HTTP.'
        : 'Scan with your phone camera, then tap View in Room on the product page. Camera AR requires a supported phone.'));
      const qrHolder = node('div', 'jcrf-qr');
      const status = node('p', 'jcrf-message', 'Preparing QR code...');
      status.setAttribute('role', 'status');
      const link = node('a', 'jcrf-qr-link', product.galleryUrl ? 'Open chair gallery' : product.previewOnly ? 'Open 3D preview' : 'Open product page');
      link.href = url.href;
      content.append(qrHolder, status, link);
      loadQr(product.qrScript).then(createQr => {
        if (!overlay.isConnected) return;
        const qr = createQr(0, 'M');
        qr.addData(url.href);
        qr.make();
        qrHolder.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 16, scalable: true, alt: 'QR code to open this page on your phone' });
        status.textContent = 'QR code ready to scan.';
      }).catch(error => {
        console.error('JC RoomFit: QR code unavailable', error);
        if (overlay.isConnected) status.textContent = 'The QR code could not be generated. Open the product link on your phone instead.';
      });
      return;
    }
    if (mode === 'model') {
      const model = product.model;
      if (!model) {
        content.append(node('p', 'jcrf-message', 'A 3D model is not available for this chair yet.'));
      } else if (model.url) {
        const viewer = node('model-viewer', 'jcrf-viewer');
        viewer.setAttribute('src', model.url);
        viewer.setAttribute('camera-controls', '');
        viewer.setAttribute('auto-rotate', '');
        viewer.setAttribute('interaction-prompt', 'auto');
        viewer.setAttribute('alt', model.demo ? 'Fictional demo chair model. Not a JC Buckman product.' : '3D model of ' + product.title);
        viewer.setAttribute('loading', 'lazy');
        if (model.demo) content.append(node('p', 'jcrf-demo-note', 'Generic CC0 demo chair — not a JC Buckman massage chair. AR is disabled for the sample.'));
        content.append(viewer);
        demoViewer().catch(cause => {
          console.error('JC RoomFit: demo model viewer unavailable', cause);
          viewer.replaceWith(node('p', 'jcrf-message', 'The 3D viewer could not load. Please check your connection.'));
        });
      } else {
        const template = document.getElementById(model.templateId);
        if (template && template.content) {
          const fragment = template.content.cloneNode(true);
          content.append(fragment);
          const viewer = content.querySelector('model-viewer');
          if (viewer) {
            viewer.classList.add('jcrf-viewer');
            if (window.Shopify && window.Shopify.loadFeatures) {
              window.Shopify.loadFeatures([{
                name: 'model-viewer-ui',
                version: '1.0',
                onLoad: function (error) {
                  if (error) { console.error('JC RoomFit: Shopify model viewer could not load', error); return; }
                  if (window.Shopify.ModelViewerUI) new window.Shopify.ModelViewerUI(viewer);
                }
              }]);
            }
          }
          const nativeAr = content.querySelector('[data-shopify-xr]');
          if (nativeAr) nativeAr.hidden = !canUseMobileAR();
          if (!canUseMobileAR()) content.append(node('p', 'jcrf-message', 'AR viewing is available on supported mobile devices.'));
        } else {
          content.append(node('p', 'jcrf-message', 'The 3D model could not be loaded. Please view the product page.'));
        }
      }
      return;
    }
    const form = node('form', 'jcrf-form');
    const widthLabel = node('label', '', 'Available Room Width (cm)');
    const widthInput = node('input');
    widthInput.type = 'number'; widthInput.name = 'roomWidth'; widthInput.min = '1'; widthInput.max = '2000'; widthInput.step = 'any'; widthInput.required = true; widthInput.inputMode = 'decimal';
    const depthLabel = node('label', '', 'Available Room Depth (cm)');
    const depthInput = node('input');
    depthInput.type = 'number'; depthInput.name = 'roomDepth'; depthInput.min = '1'; depthInput.max = '2000'; depthInput.step = 'any'; depthInput.required = true; depthInput.inputMode = 'decimal';
    widthLabel.append(widthInput); depthLabel.append(depthInput);
    const submit = node('button', 'jcrf-button', 'Check space');
    submit.type = 'submit';
    const outcome = node('div', 'jcrf-outcome');
    outcome.setAttribute('role', 'status');
    outcome.setAttribute('aria-live', 'polite');
    form.append(widthLabel, depthLabel, submit, outcome);
    content.append(form);
    form.addEventListener('submit', event => {
      event.preventDefault();
      const result = evaluate(product.dimensions, Number(widthInput.value), Number(depthInput.value));
      outcome.replaceChildren(node('strong', '', {
        great: 'GREAT FIT', tight: 'TIGHT FIT', 'more-space': 'MORE SPACE RECOMMENDED',
        unknown: 'DIMENSIONS UNAVAILABLE', invalid: 'CHECK YOUR MEASUREMENTS'
      }[result.status]), node('p', '', result.message));
      if (result.required) {
        outcome.append(node('p', 'jcrf-footprint',
          'Required operating space: ' + result.required.width + ' cm wide × ' + result.required.depth +
          ' cm deep. Chair height: ' + product.dimensions.chairHeight + ' cm (vertical clearance not checked).'));
      }
      outcome.dataset.jcrfStatus = result.status;
      if (typeof product.onFit === 'function') product.onFit(result);
    });
    widthInput.focus();
  }

  function attach(button, product, mode) {
    button.addEventListener('click', () => open(product, mode, button));
  }

  function initProduct(container) {
    container.querySelectorAll('[data-jc-roomfit-product]:not([data-jcrf-ready])').forEach(root => {
      root.dataset.jcrfReady = 'true';
      const data = root.querySelector('[data-jc-roomfit-data]');
      let product;
      try { product = JSON.parse(data.textContent); }
      catch (cause) {
        console.error('JC RoomFit: product data unavailable', cause);
        root.insertAdjacentText('beforeend', 'RoomFit is temporarily unavailable.');
        return;
      }
      const template = root.querySelector('template[data-jc-roomfit-model]');
      if (template) product.model = { templateId: template.id };
      root.querySelectorAll('[data-jc-roomfit-open]').forEach(button => attach(button, product, 'fit'));
      root.querySelectorAll('[data-jc-roomfit-view]').forEach(button => attach(button, product, 'model'));
      root.querySelectorAll('[data-jc-roomfit-qr]').forEach(button => attach(button, product, 'qr'));
      if (new URLSearchParams(window.location.search).has('jc_roomfit_ar')) {
        const hint = root.querySelector('[data-jc-roomfit-ar-hint]');
        if (hint) hint.hidden = false;
      }
    });
  }

  function initHome(container) {
    container.querySelectorAll('[data-jc-roomfit-home]:not([data-jcrf-ready])').forEach(button => {
      const data = button.parentElement.querySelector('[data-jc-roomfit-home-data]');
      let product;
      try {
        if (!data) throw new Error('Homepage RoomFit data is missing');
        product = JSON.parse(data.textContent);
        if (!product.galleryUrl || !product.qrScript) throw new Error('Homepage chair gallery is unavailable');
      } catch (cause) {
        console.error('JC RoomFit: homepage chair unavailable', cause);
        button.insertAdjacentText('afterend', 'Room preview is temporarily unavailable.');
        button.disabled = true;
        return;
      }
      button.dataset.jcrfReady = 'true';
      attach(button, product, 'qr');
    });
  }

  window.JCRoomFit = { evaluate, requirements, open, attach };
  function init(container) {
    initProduct(container);
    initHome(container);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
}());
